import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMessage, ORG_LOGOS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created. Continue your application.");
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-12" data-testid="register-page">
      <div className="max-w-xl mx-auto px-4">
        <Card className="p-8 border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <img src={ORG_LOGOS.ACIS} alt="ACIS" className="h-9 w-9 object-contain" />
              <img src={ORG_LOGOS.IBTHM} alt="IBTHM" className="h-9 w-9 object-contain" />
            </div>
            <div>
              <div className="font-heading font-bold text-primary text-base">ACIS | IBTHM</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Applicant portal</div>
            </div>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Create an account</div>
          <h1 className="font-heading text-2xl font-bold text-primary">Begin your credentialing application.</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            You can save your progress at any stage. Your credential application is reviewed by an
            independent committee.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Full legal name</Label>
              <Input data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">Email</Label>
                <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div>
                <Label className="mb-1.5 block">Phone</Label>
                <Input data-testid="register-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Password</Label>
              <Input
                data-testid="register-password"
                type="password"
                minLength={8}
                required
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
              />
              <div className="text-xs text-slate-500 mt-1">Minimum 8 characters.</div>
            </div>
            {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
            <Button data-testid="register-submit" disabled={loading} className="w-full h-11 bg-primary text-white hover:bg-[#113A64]">
              {loading ? "Creating account…" : "Create applicant account"}
            </Button>
            <div className="text-center text-sm text-slate-600">
              Already registered?{" "}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Sign in
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
